import type { DocumentDto } from '@dbrain/shared';
import { Button, Spinner } from '@dbrain/ui';
import { Fragment, useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { usePart, useSheets } from '../../api/documents.js';
import { currentLocale } from '../../i18n/index.js';

/**
 * Pieces shared by the library, a conversation's documents and the sources
 * panel (Phase 12; DOC-001…DOC-008): the type badge, the reading state with
 * the size estimate and what was done (DOC-002, DOC-008, QW-BUG-023), and the
 * preview that opens at a citation's page, slide, sheet rows or section with
 * the quoted passage marked (DOC-004).
 */
const BADGES: Record<string, { label: string; tone: string }> = {
  pdf: { label: 'PDF', tone: 'red' },
  docx: { label: 'DOCX', tone: 'blue' },
  pptx: { label: 'PPTX', tone: 'orange' },
  xlsx: { label: 'XLSX', tone: 'green' },
  csv: { label: 'CSV', tone: 'green' },
  markdown: { label: 'MD', tone: 'gray' },
  text: { label: 'TXT', tone: 'gray' },
  image: { label: 'IMG', tone: 'purple' },
  other: { label: 'DOC', tone: 'gray' },
};

/** The badge for a document kind, or for a file name's extension before the document exists. */
export function KindBadge({ kind, name }: { kind?: string | null; name?: string }) {
  const ext = (name ?? '').split('.').pop()?.toLowerCase() ?? '';
  const guess = kind ?? (['xls', 'ods'].includes(ext) ? 'xlsx' : ['doc', 'odt'].includes(ext) ? 'docx' : ['ppt', 'odp'].includes(ext) ? 'pptx' : ['md'].includes(ext) ? 'markdown' : ['png', 'jpg', 'jpeg', 'gif', 'webp', 'tif', 'tiff'].includes(ext) ? 'image' : ext);
  const badge = BADGES[guess] ?? BADGES.other;
  return (
    <span className={`kind-badge kind-badge--${badge?.tone}`} aria-hidden="true">
      {badge?.label}
    </span>
  );
}

const number = (value: number) => value.toLocaleString(currentLocale());

/** "8 sayfa", "2 sayfa", "12 slayt", "~1.200 token": how big a document is. */
export function documentSize(doc: DocumentDto, t: (key: string, options?: Record<string, unknown>) => string): string | null {
  if (doc.kind === 'xlsx' || doc.kind === 'csv') return doc.sheets !== null ? t('documents.size.sheets', { count: doc.sheets }) : null;
  if (doc.kind === 'pptx') return doc.pages !== null ? t('documents.size.slides', { count: doc.pages }) : null;
  if (doc.kind === 'text' || doc.kind === 'markdown') return doc.tokens !== null ? t('documents.size.tokens', { tokens: number(doc.tokens) }) : null;
  return doc.pages !== null ? t('documents.size.pages', { count: doc.pages }) : null;
}

/**
 * Where a document is: waiting, being read (with progress), ready, or why it could not be read. A Microsoft 365
 * copy says "İçe aktarılıyor" (or "Yeniden alınıyor…") while it is fetched and "Alınamadı" when it could not be.
 */
export function DocumentState({ doc }: { doc: DocumentDto }) {
  const { t } = useTranslation();
  const importing = doc.stage === 'importing';
  if (doc.status === 'failed') {
    return (
      <span className="doc-state doc-state--failed" data-testid="doc-state" data-status="failed">
        {t(importing ? 'documents.state.importFailed' : 'documents.state.failed', {
          reason: t(`documents.errors.${doc.errorCode ?? 'processing_failed'}`, { defaultValue: t('documents.errors.processing_failed') }),
        })}
      </span>
    );
  }
  if (doc.status !== 'ready') {
    const progress = doc.progress ? ` (${doc.progress.done}/${doc.progress.total})` : '';
    const words = importing
      ? t(doc.source?.fetchedAt ? 'documents.sourceState.refetching' : 'documents.stage.importing')
      : doc.status === 'queued'
        ? t('documents.state.queued')
        : `${t(`documents.stage.${doc.stage ?? 'extracting'}`)}${progress}`;
    return (
      <span className="doc-state doc-state--busy" data-testid="doc-state" data-status={doc.status}>
        <Spinner label="" />
        {words}
      </span>
    );
  }
  return (
    <span className="doc-state doc-state--ready" data-testid="doc-state" data-status="ready">
      {t('documents.state.ready')}
    </span>
  );
}

/** DOC-002: what reading it costs, said before the first question. */
export function EstimateLine({ doc }: { doc: DocumentDto }) {
  const { t } = useTranslation();
  if (!doc.estimate) return null;
  return (
    <span className="muted doc-estimate" data-testid="doc-estimate">
      {doc.estimate.fitsContext
        ? t('documents.estimate.fits', { tokens: number(doc.estimate.tokens), question: number(doc.estimate.questionTokens) })
        : t('documents.estimate.large', { tokens: number(doc.estimate.tokens), question: number(doc.estimate.questionTokens) })}
    </span>
  );
}

/** DOC-008, QW-BUG-023: text recognition, its quality, parts not read: said up front. */
export function DocumentNotes({ doc }: { doc: DocumentDto }) {
  const { t } = useTranslation();
  const notes = doc.notes.filter((n) => n.code !== 'ocr_used' || !doc.notes.some((m) => m.code === 'ocr_low_quality'));
  if (notes.length === 0) return null;
  const quality = doc.ocr ? t(`documents.quality.${doc.ocr.quality}`) : '';
  return (
    <ul className="doc-notes" data-testid="doc-notes">
      {notes.map((note) => (
        <li key={note.code} className={note.code === 'ocr_low_quality' || note.code === 'no_text' || note.code === 'image_no_text' ? 'doc-notes__warn' : undefined}>
          {t(`documents.notes.${note.code}`, { ...(note.params ?? {}), quality, defaultValue: note.code })}
        </li>
      ))}
    </ul>
  );
}

/** The quoted passage inside a text, marked; the text alone when the quote is not found in it. */
export function Highlighted({ text, quote }: { text: string; quote?: string | null }) {
  const ref = useRef<HTMLElement>(null);
  useEffect(() => {
    ref.current?.scrollIntoView?.({ block: 'center' });
  }, [quote]);
  if (!quote) return <>{text}</>;
  const flat = (value: string) => value.replace(/\s+/g, ' ');
  const source = flat(text);
  const needle = flat(quote).replace(/…$/, '').trim();
  const at = needle ? source.indexOf(needle) : -1;
  if (at < 0) return <>{source}</>;
  return (
    <>
      {source.slice(0, at)}
      <mark ref={ref} className="quote-mark" data-testid="quote-mark">
        {source.slice(at, at + needle.length)}
      </mark>
      {source.slice(at + needle.length)}
    </>
  );
}

export interface PreviewFocus {
  kind: 'page' | 'sheet' | 'slide' | 'section';
  number: number;
  label?: string | null;
  rowFrom?: number | null;
  rowTo?: number | null;
  quote?: string | null;
}

function PagePreview({ doc, focus }: { doc: DocumentDto; focus: PreviewFocus | null }) {
  const { t } = useTranslation();
  const total = doc.pages ?? 1;
  const [page, setPage] = useState(focus?.number ?? 1);
  useEffect(() => setPage(focus?.number ?? 1), [focus?.number, doc.id]);
  const part = usePart(doc.id, page, Boolean(focus?.quote) && page === focus?.number);
  const slides = doc.kind === 'pptx';
  return (
    <div className="doc-preview doc-preview--pages" data-testid="doc-preview" data-page={page}>
      <div className="doc-preview__bar">
        <Button size="sm" variant="ghost" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} aria-label={t('documents.preview.previous')}>
          ‹
        </Button>
        <span data-testid="doc-preview-position">{t(slides ? 'documents.preview.slideOf' : 'documents.preview.pageOf', { page, total })}</span>
        <Button size="sm" variant="ghost" disabled={page >= total} onClick={() => setPage((p) => Math.min(total, p + 1))} aria-label={t('documents.preview.next')}>
          ›
        </Button>
        <a className="doc-preview__open" href={`/api/documents/${doc.id}/pdf#page=${page}`} target="_blank" rel="noopener noreferrer">
          {t('documents.preview.openFull')}
        </a>
      </div>
      {/* the quoted passage first: the page picture is small at the panel's width */}
      {focus?.quote && page === focus.number ? (
        <figure className="doc-preview__quote" data-testid="doc-quote">
          <figcaption className="muted">{t('documents.preview.quote')}</figcaption>
          <p>
            <Highlighted text={part.data?.[0]?.text ?? focus.quote} quote={focus.quote} />
          </p>
        </figure>
      ) : null}
      <img className="doc-preview__page" src={`/api/documents/${doc.id}/pages/${page}/image`} alt={t(slides ? 'documents.preview.slideOf' : 'documents.preview.pageOf', { page, total })} loading="lazy" />
    </div>
  );
}

function SheetsPreview({ doc, focus }: { doc: DocumentDto; focus: PreviewFocus | null }) {
  const { t } = useTranslation();
  const sheets = useSheets(doc.id, true);
  const [active, setActive] = useState<string | null>(null);
  const marked = useRef<HTMLTableRowElement>(null);
  const list = sheets.data ?? [];
  const current = list.find((s) => s.name === (active ?? focus?.label)) ?? list[0];
  useEffect(() => setActive(null), [focus?.label, focus?.rowFrom]);
  useEffect(() => {
    marked.current?.scrollIntoView?.({ block: 'center' });
  }, [current?.name, focus?.rowFrom, sheets.data]);
  if (sheets.isPending) return <Spinner label={t('common.loading')} />;
  if (!current) return <p className="muted">{t('documents.preview.empty')}</p>;
  const inFocus = (row: number) => focus?.kind === 'sheet' && current.name === focus.label && focus.rowFrom != null && row >= focus.rowFrom && row <= (focus.rowTo ?? focus.rowFrom);
  return (
    <div className="doc-preview doc-preview--sheets" data-testid="doc-preview">
      {list.length > 1 ? (
        <div className="doc-preview__tabs" role="tablist">
          {list.map((sheet) => (
            <button key={sheet.name} type="button" role="tab" aria-selected={sheet.name === current.name} className={sheet.name === current.name ? 'pill pill--active' : 'pill'} onClick={() => setActive(sheet.name)}>
              {sheet.name}
            </button>
          ))}
        </div>
      ) : null}
      <div className="table-frame doc-preview__table">
        <table className="md-table">
          <tbody>
            {current.rows.map(([row, cells]) => (
              <tr key={row} ref={inFocus(row) && row === focus?.rowFrom ? marked : undefined} className={inFocus(row) ? 'row--cited' : undefined} data-row={row}>
                <th scope="row" className="muted">
                  {row}
                </th>
                {Array.from({ length: current.columns }, (_, i) => (
                  <td key={i}>{cells[i] ?? ''}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {current.truncated ? <p className="muted">{t('documents.preview.truncated')}</p> : null}
    </div>
  );
}

function TextPreview({ doc, focus }: { doc: DocumentDto; focus: PreviewFocus | null }) {
  const { t } = useTranslation();
  const target = focus?.number ?? 1;
  const part = usePart(doc.id, target, true);
  if (part.isPending) return <Spinner label={t('common.loading')} />;
  const text = part.data?.[0]?.text ?? '';
  return (
    <div className="doc-preview doc-preview--text" data-testid="doc-preview">
      {part.data?.[0]?.label ? <p className="doc-preview__label">{part.data[0].label}</p> : null}
      <p className="doc-preview__text">
        <Highlighted text={text} quote={focus?.quote ?? null} />
      </p>
    </div>
  );
}

function ImagePreview({ doc, focus }: { doc: DocumentDto; focus: PreviewFocus | null }) {
  const { t } = useTranslation();
  const part = usePart(doc.id, 1, true);
  return (
    <div className="doc-preview doc-preview--image" data-testid="doc-preview">
      <img className="doc-preview__page" src={`/api/documents/${doc.id}/file?inline=1`} alt={doc.name} />
      {part.data?.[0]?.text ? (
        <figure className="doc-preview__quote">
          <figcaption className="muted">{t('documents.preview.recognized')}</figcaption>
          <p>
            <Highlighted text={part.data[0].text} quote={focus?.quote ?? null} />
          </p>
        </figure>
      ) : null}
    </div>
  );
}

/** DOC-006: a document's preview, opened at a position when a citation points to one. */
export function DocumentPreview({ doc, focus }: { doc: DocumentDto; focus: PreviewFocus | null }): ReactNode {
  const { t } = useTranslation();
  if (doc.status === 'failed') return <DocumentState doc={doc} />;
  switch (doc.preview) {
    case 'pdf':
      return <PagePreview doc={doc} focus={focus} />;
    case 'sheets':
      return <SheetsPreview doc={doc} focus={focus} />;
    case 'text':
      return <TextPreview doc={doc} focus={focus} />;
    case 'image':
      return <ImagePreview doc={doc} focus={focus} />;
    default:
      return (
        <Fragment>
          <DocumentState doc={doc} />
          <p className="muted">{t('documents.preview.notYet')}</p>
        </Fragment>
      );
  }
}
